import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";

import { AutocompleteLabelField, InputField } from "@/features/FormCard/Fields";

import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";

import { FormActionsNew } from "@/features/FormCard/Actions";

import {
  adjustDataForSubmission,
  countries_options,
  preprocessCountryData,
} from "@/components/Form/CountryPhone/data";
import { useEditForm } from "@/features/Edit/hooks";
import { NewDiff } from "@/features/FormCard/NewDiff";
import { Label } from "@radix-ui/react-label";
import { useTranslation } from "react-i18next";

const FormSchema = z.object({
  name: z.string().min(1, "Le nom est requis"),
  country: z.string().optional().nullable(),
  sector: z.string().optional().nullable(),
});

const client = "a9";
const table_name = "a9_issuers";

export function A9IssuersNew() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    options,
  } = useEditForm({
    client,
    table_name,
    FormSchema,
    isNew: true,
    adjustData: adjustDataForSubmission,
    adjustOnReset: preprocessCountryData,
  });

  const navigateToList = () => {
    navigate(-1);
  };

  return (
    <div className="grid place-items-center bg-gray-50 h-full w-full">
      <div className="flex justify-center w-full max-w-card">
        <Card className="p-4 bg-white w-full ">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t("actions.new")}{" "}
                {t(`${table_name}.title`, {
                  ns: "tables",
                  defaultValue: "Émetteur",
                })}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                <Label>
                  {t(`${table_name}.sections.issuer_information`, {
                    ns: "tables",
                    defaultValue: "Informations émetteur",
                  })}
                </Label>
                <div className="grid grid-cols-1 gap-4">
                  <InputField
                    name="name"
                    label={t(`${table_name}.columns.name`, {
                      ns: "tables",
                      defaultValue: "Nom",
                    })}
                    isRequired={true}
                    control={form.control}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AutocompleteLabelField
                    name="country"
                    label={t(`${table_name}.columns.country`, {
                      ns: "tables",
                      defaultValue: "Country",
                    })}
                    options={countries_options}
                    control={form.control}
                  />
                  <AutocompleteLabelField
                    name="sector"
                    label={t(`${table_name}.columns.sector`, {
                      ns: "tables",
                      defaultValue: "Secteur",
                    })}
                    options={
                      options?.["sector"]?.map((opt) => ({
                        value: opt.value,
                        label: opt.value,
                      })) ?? []
                    }
                    control={form.control}
                  />
                </div>
              </CardBody>
              <CardFooter>
                <FormActionsNew
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                />
              </CardFooter>
            </form>
          </Form>
        </Card>

        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          isLoading={isUpdating}
          onClose={cancelUpdate}
          onConfirm={confirmUpdate}
          title={t("confirm_create")}
          children={
            changedFields
              ? NewDiff(changedFields, [], undefined, table_name)
              : t("no_changes")
          }
        />

        <CancelConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onCancel={navigateToList}
        />
      </div>
    </div>
  );
}
